import React from "react";
import { Handle, Position, useReactFlow } from "reactflow";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  Group as GroupIcon,
} from "@mui/icons-material";
import BaseInstance from "../../../../Services/AxiosInstance/BaseInstance";
import { AGENT, MODELS } from "../../../../Services/Endpoint";
import { Agent } from "../../../../Shared-ui/Types/Agent";
import { MultiAgent } from "../../../../Shared-ui/Types/MultiAgent";
import Template from "../../../../Shared-ui/Types/Template";

interface CustomNodeProps {
  data: {
    label: string;
    category: string;
    description: string;
    teamName?: string;
    teamData?: MultiAgent;
    agents?: Agent[];
    provider?: string;
    model?: string;
    apiKey?: string;
    models?: string[];
    template?: Template;
  };
  id: string;
  onDelete?: (nodeId: string) => void;
}

const DeleteButton: React.FC<{ nodeId: string; onDelete?: (nodeId: string) => void; isAgentNode?: boolean }> = ({ nodeId, onDelete, isAgentNode = false }) => {
  const { deleteElements } = useReactFlow();

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    deleteElements({ nodes: [{ id: nodeId }] });
  };

  // Different positioning for TeamNode to avoid gear icon conflict
  const buttonStyle = isAgentNode ? {
    position: 'absolute' as const,
    top: 2,
    right: 2, // Position on the right side for TeamNode to avoid gear icon conflict
    width: 16,
    height: 16,
  } : {
    position: 'absolute' as const,
    top: 2,
    right: 2, // Position on the right side for other nodes
    width: 16,
    height: 16,
  };

  return (
    <Tooltip title="Delete Node" placement="top">
      <IconButton
        size="small"
        onClick={handleDelete}
        sx={{
          ...buttonStyle,
          padding: '2px',
          minWidth: '16px',
          backgroundColor: 'transparent',
          color: 'white',
          border: 'none',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            color: 'white',
          },
          zIndex: 1000,
          fontSize: '12px',
          boxShadow: 'none',
        }}
      >
        <DeleteIcon sx={{ fontSize: '12px' }} />
      </IconButton>
    </Tooltip>
  );
};

export const TeamNode: React.FC<CustomNodeProps> = React.memo(({ data, id, onDelete }) => {
  const [teamName, setTeamName] = React.useState(data.teamName || '');
  const [teamDescription, setTeamDescription] = React.useState(data.description || '');
  const [agents, setAgents] = React.useState<Agent[]>(data.agents || []);
  const [showAgentsDialog, setShowAgentsDialog] = React.useState(false);
  const [availableAgents, setAvailableAgents] = React.useState<Agent[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [settingsOpen, setSettingsOpen] = React.useState(false);

  // Initialize agents from data.agents if available
  React.useEffect(() => {
    if (data.agents && Array.isArray(data.agents) && data.agents.length > 0) {
      setAgents(data.agents);
    }
  }, [data.agents]);

  // Update data when teamName or teamDescription changes
  React.useEffect(() => {
    if (data.teamName !== teamName || data.description !== teamDescription) {
      data.teamName = teamName;
      data.description = teamDescription;
      data.label = teamName;
    }
  }, [teamName, teamDescription, data]);

  // Team settings state
  const [teamSettings, setTeamSettings] = React.useState(() => {
    const teamData = data.teamData;
    return {
      show_tool_calls: teamData?.show_tool_calls || true,
      is_shared: teamData?.is_shared || false,
      markdown: teamData?.markdown || true,
      team_mode: teamData?.team_mode || 'route',
      streaming: teamData?.team_metadata?.streaming || true,
      enable_zeasecure: teamData?.team_metadata?.enable_zeasecure || false,
      zeasecure_api_key: teamData?.team_metadata?.zeasecure_api_key || '',
      user_context_awareness: teamData?.team_metadata?.user_context_awareness || false,
    };
  });

  // Update data when agents change
  React.useEffect(() => {
    if (data.agents !== agents) {
      data.agents = agents;
    }
  }, [agents, data]);

  // Load available agents
  React.useEffect(() => {
    const loadAgents = async () => {
      try {
        setLoading(true);
        const response = await BaseInstance.get(`${AGENT}?page=1&page_size=100`);
        const allAgents = response.data.items || [];
        // Filter out agents that are already in the team
        const filteredAgents = allAgents.filter((agent: Agent) =>
          !agents.some(teamAgent => teamAgent.id === agent.id)
        );
        setAvailableAgents(filteredAgents);
      } catch (error) {
        console.error('Failed to load agents:', error);
      } finally {
        setLoading(false);
      }
    };

    if (showAgentsDialog) {
      loadAgents();
    }
  }, [showAgentsDialog, agents]);

  const handleAddAgent = (agent: Agent) => {
    setAgents(prev => [...prev, agent]);
    setAvailableAgents(prev => prev.filter(a => a.id !== agent.id));
  };

  const handleRemoveAgent = (agentId: string | undefined) => {
    if (!agentId) return;
    const agentToRemove = agents.find(agent => agent.id === agentId);
    if (agentToRemove) {
      setAgents(prev => prev.filter(agent => agent.id !== agentId));
      setAvailableAgents(prev => [...prev, agentToRemove]);
    }
  };

  const handleSettingsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setTeamSettings(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleSelectChange = (event: any) => {
    const { name, value } = event.target;
    setTeamSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <>
      <div
        style={{
          width: 200,
          backgroundColor: 'white',
          border: '1px solid #666666',
          borderRadius: 4,
          boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <DeleteButton nodeId={id} onDelete={onDelete} isAgentNode={true} />

        {/* Header */}
        <div
          style={{
            background: 'linear-gradient(135deg, #666666 0%, #888888 100%)',
            padding: '3px 6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 3,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <IconButton
              size="small"
              onClick={() => setSettingsOpen(true)}
              style={{
                padding: '2px',
                minWidth: '16px',
                width: '16px',
                height: '16px',
              }}
            >
              <SettingsIcon style={{ fontSize: '12px', color: 'white' }} />
            </IconButton>
            <span style={{ fontWeight: 'bold', color: 'white', fontSize: '10px' }}>
              Team
            </span>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: 6 }}>
          {/* Team Name */}
          <div style={{ marginBottom: 6 }}>
            <label style={{ fontSize: '8px', color: '#666', fontWeight: 'bold', display: 'block', marginBottom: 1 }}>
              Team Name
            </label>
            <input
              type="text"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              style={{
                width: '100%',
                padding: '3px 5px',
                border: '1px solid #ddd',
                borderRadius: 2,
                backgroundColor: '#f5f5f5',
                fontSize: '9px',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Team Description */}
          <div style={{ marginBottom: 6 }}>
            <label style={{ fontSize: '8px', color: '#666', fontWeight: 'bold', display: 'block', marginBottom: 1 }}>
              Description
            </label>
            <textarea
              value={teamDescription}
              onChange={(e) => setTeamDescription(e.target.value)}
              placeholder="Enter team description..."
              style={{
                width: '100%',
                padding: '3px 5px',
                border: '1px solid #ddd',
                borderRadius: 2,
                backgroundColor: '#f5f5f5',
                fontSize: '9px',
                boxSizing: 'border-box',
                resize: 'vertical',
                minHeight: '40px',
                fontFamily: 'inherit',
              }}
            />
          </div>

          {/* Agents Section */}
          <div style={{ marginBottom: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <label style={{ fontSize: '8px', color: '#666', fontWeight: 'bold' }}>
                Agents ({agents.length > 0 ? agents.length : (data.teamData?.agent_count || 0)})
              </label>
              <button
                onClick={() => setShowAgentsDialog(true)}
                style={{
                  fontSize: '7px',
                  padding: '2px 4px',
                  border: '1px solid #ccc',
                  borderRadius: '2px',
                  backgroundColor: '#e3f2fd',
                  cursor: 'pointer',
                  color: '#1976d2',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2px'
                }}
              >
                <AddIcon sx={{ fontSize: '8px' }} />
                Add
              </button>
            </div>

            {/* Display agents */}
            {agents.length > 0 ? (
              <div style={{
                maxHeight: '120px',
                overflowY: 'auto',
                border: '1px solid #eee',
                borderRadius: '4px',
                padding: '4px'
              }}>
                {agents.map((agent, index) => (
                  <div key={agent.id} style={{
                    fontSize: '7px',
                    marginBottom: '2px',
                    padding: '2px',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '2px',
                    border: '1px solid #e0e0e0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <div style={{ fontWeight: 'bold', color: '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '140px' }}>
                        {agent.name}
                      </div>
                      <div style={{ color: '#666', fontSize: '6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '140px' }}>
                        {agent.description}
                      </div>
                    </div>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveAgent(agent.id)}
                      sx={{ fontSize: '8px', color: '#ff4444' }}
                    >
                      <DeleteIcon sx={{ fontSize: '8px' }} />
                    </IconButton>
                  </div>
                ))}
              </div>
            ) : data.teamData?.agent_ids && data.teamData.agent_ids.length > 0 ? (
              <div style={{
                maxHeight: '120px',
                overflowY: 'auto',
                border: '1px solid #eee',
                borderRadius: '4px',
                padding: '4px',
                backgroundColor: '#f5f5f5'
              }}>
                <div style={{
                  fontSize: '7px',
                  color: '#666',
                  fontStyle: 'italic',
                  textAlign: 'center',
                  padding: '8px'
                }}>
                  {data.teamData.agent_ids.length} agent(s) configured
                  <br />
                  <span style={{ fontSize: '6px' }}>
                    Click "Add" to manage agents
                  </span>
                </div>
              </div>
            ) : null}
          </div>

          {/* Input Section */}
          <div>
            <h4 style={{
              margin: '0 0 3px 0',
              textAlign: 'center',
              fontSize: '9px',
              fontWeight: 'bold',
              color: '#333'
            }}>
              Input
            </h4>

            <div className="handle-label-pair" style={{ justifyContent: 'flex-start', marginBottom: 4 }}>
              <Handle
                type="target"
                position={Position.Left}
                id="llm-input"
                style={{
                  background: '#666666',
                  width: 8,
                  height: 8,
                  border: '1px solid white',
                  borderRadius: '50%',
                }}
              />
              <label style={{ fontSize: '9px', color: '#666666', fontWeight: 'medium' }}>
                LLM
              </label>
            </div>

            <div className="handle-label-pair" style={{ justifyContent: 'flex-start' }}>
              <Handle
                type="target"
                position={Position.Left}
                id="prompt-input"
                style={{
                  background: '#666666',
                  width: 8,
                  height: 8,
                  border: '1px solid white',
                  borderRadius: '50%',
                }}
              />
              <label style={{ fontSize: '9px', color: '#666666', fontWeight: 'medium' }}>
                Prompt
              </label>
            </div>
          </div>

          {/* Output Section */}
          <div>
            <h4 style={{
              margin: '0 0 3px 0',
              textAlign: 'center',
              fontSize: '9px',
              fontWeight: 'bold',
              color: '#333'
            }}>
              Output
            </h4>

            <div className="handle-label-pair" style={{ justifyContent: 'flex-end' }}>
              <label style={{ fontSize: '9px', color: '#333', fontWeight: 'medium' }}>
                Response
              </label>
              <Handle
                type="source"
                position={Position.Right}
                id="output"
                style={{
                  background: '#1976d2',
                  width: 8,
                  height: 8,
                  border: '1px solid white',
                  borderRadius: '50%',
                }}
              />
            </div>
          </div>
        </div>

        {/* Agents Configuration Dialog */}
        <Dialog
          open={showAgentsDialog}
          onClose={() => setShowAgentsDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Add Agents to Team</Typography>
              <IconButton onClick={() => setShowAgentsDialog(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {/* Available Agents */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Available Agents ({availableAgents.length})
                </Typography>
                {loading ? (
                  <Typography>Loading agents...</Typography>
                ) : availableAgents.length > 0 ? (
                  <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                    {availableAgents.map((agent) => (
                      <ListItem
                        key={agent.id}
                        sx={{
                          border: '1px solid #e0e0e0',
                          borderRadius: 1,
                          mb: 1,
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: '#f5f5f5',
                            borderColor: '#1976d2',
                          },
                        }}
                        onClick={() => handleAddAgent(agent)}
                      >
                        <ListItemText
                          primary={agent.name}
                          secondary={agent.description}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={() => handleAddAgent(agent)}
                            color="primary"
                          >
                            <AddIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography color="text.secondary">
                    No available agents to add
                  </Typography>
                )}
              </Box>

              {/* Current Team Agents */}
              {agents.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Team Agents ({agents.length})
                  </Typography>
                  <List sx={{ maxHeight: 200, overflow: 'auto' }}>
                    {agents.map((agent) => (
                      <ListItem
                        key={agent.id}
                        sx={{
                          border: '1px solid #e0e0e0',
                          borderRadius: 1,
                          mb: 1,
                        }}
                      >
                        <ListItemText
                          primary={agent.name}
                          secondary={agent.description}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={() => handleRemoveAgent(agent.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Stack>
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setShowAgentsDialog(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Settings Dialog */}
        <Dialog
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Team Settings</Typography>
              <IconButton onClick={() => setSettingsOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Team Mode</InputLabel>
                <Select
                  value={teamSettings.team_mode}
                  onChange={handleSelectChange}
                  name="team_mode"
                  label="Team Mode"
                >
                  <MenuItem value="route">Route</MenuItem>
                  <MenuItem value="coordinate">Coordinate</MenuItem>
                  <MenuItem value="collaborate">Collaborate</MenuItem>
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Switch
                    checked={teamSettings.show_tool_calls}
                    onChange={handleSettingsChange}
                    name="show_tool_calls"
                  />
                }
                label="Show Tool Calls"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={teamSettings.is_shared}
                    onChange={handleSettingsChange}
                    name="is_shared"
                  />
                }
                label="Is Shared"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={teamSettings.markdown}
                    onChange={handleSettingsChange}
                    name="markdown"
                  />
                }
                label="Markdown"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={teamSettings.streaming}
                    onChange={handleSettingsChange}
                    name="streaming"
                  />
                }
                label="Streaming"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={teamSettings.user_context_awareness}
                    onChange={handleSettingsChange}
                    name="user_context_awareness"
                  />
                }
                label="User Context Awareness"
              />
            </Stack>
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setSettingsOpen(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </>
  );
});

export const LLMNode: React.FC<CustomNodeProps> = React.memo(({ data, id, onDelete }) => {
  const [provider] = React.useState(data.provider || 'OpenAI'); // Provider is fixed, no setter needed
  const [model, setModel] = React.useState(data.model || 'gpt-4o');
  const [apiKey, setApiKey] = React.useState(data.apiKey || '');
  const [models, setModels] = React.useState<string[]>(data.models || []);
  const [loading, setLoading] = React.useState(false);

  // Update data when values change
  React.useEffect(() => {
    if (data.provider !== provider || data.model !== model || data.apiKey !== apiKey) {
      data.provider = provider;
      data.model = model;
      data.apiKey = apiKey;
      data.label = `${provider}:${model}`;
    }
  }, [provider, model, apiKey, data]);

  // Load models when component mounts (provider is fixed)
  React.useEffect(() => {
    const loadModels = async () => {
      try {
        setLoading(true);
        const response = await BaseInstance.get(MODELS);
        const modelsData = response.data;

        if (modelsData[provider]) {
          setModels(modelsData[provider]);
          data.models = modelsData[provider]; // Store models in data
        } else {
          setModels([]);
          data.models = [];
        }
      } catch (error) {
        console.error('Failed to load models:', error);
        // Keep using existing models if fetch fails
        if (data.models && data.models.length > 0) {
          setModels(data.models);
        } else {
          setModels([]);
          data.models = [];
        }
      } finally {
        setLoading(false);
      }
    };

    // Always load fresh models to ensure newly added models appear
    loadModels();
  }, [provider, data]);

  return (
    <div
      style={{
        width: 200,
        backgroundColor: 'white',
        border: '1px solid #666666',
        borderRadius: 4,
        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <DeleteButton nodeId={id} onDelete={onDelete} />

      {/* Header */}
      <div
        style={{
          background: 'linear-gradient(135deg, #666666 0%, #888888 100%)',
          padding: '3px 6px',
          display: 'flex',
          alignItems: 'center',
          gap: 3,
        }}
      >
        <span style={{ fontWeight: 'bold', color: 'white', fontSize: '10px' }}>
          {provider === 'OpenAI' ? 'Models' : provider}
        </span>
      </div>

      {/* Content */}
      <div style={{ padding: 6 }}>
        {/* Model */}
        <div style={{ marginBottom: 6 }}>
          <label style={{ fontSize: '8px', color: '#666', fontWeight: 'bold', display: 'block', marginBottom: 1 }}>
            Model
          </label>
          <select
            value={model}
            onChange={(e) => setModel(e.target.value)}
            disabled={loading}
            style={{
              width: '100%',
              padding: '3px 5px',
              border: '1px solid #ddd',
              borderRadius: 2,
              backgroundColor: loading ? '#f0f0f0' : '#f5f5f5',
              fontSize: '9px',
              boxSizing: 'border-box',
            }}
          >
            {models.map((modelOption) => (
              <option key={modelOption} value={modelOption}>
                {modelOption}
              </option>
            ))}
          </select>
        </div>

        {/* API Key */}
        <div style={{ marginBottom: 6 }}>
          <label style={{ fontSize: '8px', color: '#666', fontWeight: 'bold', display: 'block', marginBottom: 1 }}>
            API Key
          </label>
          <input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="Enter API key"
            style={{
              width: '100%',
              padding: '3px 5px',
              border: '1px solid #ddd',
              borderRadius: 2,
              backgroundColor: '#f5f5f5',
              fontSize: '9px',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Output Section */}
        <div>
          <h4 style={{
            margin: '0 0 3px 0',
            textAlign: 'center',
            fontSize: '9px',
            fontWeight: 'bold',
            color: '#333'
          }}>
            Output
          </h4>

          <div className="handle-label-pair" style={{ justifyContent: 'flex-end' }}>
            <label style={{ fontSize: '9px', color: '#333', fontWeight: 'medium' }}>
              Response
            </label>
            <Handle
              type="source"
              position={Position.Right}
              id="output"
              style={{
                background: '#1976d2',
                width: 8,
                height: 8,
                border: '1px solid white',
                borderRadius: '50%',
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
});

export const CustomPromptNode: React.FC<CustomNodeProps> = React.memo(({ data, id, onDelete }) => {
  // Initialize with the content from data.template if it exists
  const [promptText, setPromptText] = React.useState(data.template?.content || '');
  const [templates, setTemplates] = React.useState<Template[]>([]);
  const [showTemplateDialog, setShowTemplateDialog] = React.useState(false);
  const [showPopup, setShowPopup] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  // Update promptText when data.template changes
  React.useEffect(() => {
    if (data.template) {
      setPromptText(data.template.content || '');
    }
  }, [data.template]);

  // Truncate text for display to improve performance
  const displayText = React.useMemo(() => {
    return promptText.length > 200
      ? promptText.substring(0, 200) + '...'
      : promptText;
  }, [promptText]);

  // Import Templates from the constant
  React.useEffect(() => {
    const loadTemplates = async () => {
      try {
        setLoading(true);
        // Import Templates dynamically to avoid circular dependency
        const { Templates } = await import('../../../../Constant/Templates');
        setTemplates(Templates);
      } catch (error) {
        console.error('Failed to load templates:', error);
        setTemplates([]);
      } finally {
        setLoading(false);
      }
    };

    loadTemplates();
  }, []);

  const handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    // Only allow editing if text is short, otherwise redirect to popup
    if (promptText.length > 200) {
      setShowPopup(true);
      return;
    }

    setPromptText(event.target.value);
    // Update the template content in the node data
    if (data.template) {
      data.template.content = event.target.value;
    }
  };

  const handleTemplateSelect = (template: Template) => {
    setPromptText(template.content);
    // Update the template content in the node data
    if (data.template) {
      data.template.content = template.content;
    }
    setShowTemplateDialog(false);
  };

  const handlePopupTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPromptText(event.target.value);
    // Update the template content in the node data
    if (data.template) {
      data.template.content = event.target.value;
    }
  };

  const handleOpenPopup = () => {
    setShowPopup(true);
  };

  const handleClosePopup = () => {
    setShowPopup(false);
  };

  return (
    <div
      style={{
        width: 200,
        backgroundColor: 'white',
        border: '1px solid #666666',
        borderRadius: 4,
        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <DeleteButton nodeId={id} onDelete={onDelete} />
      {/* Header */}
      <div
        style={{
          background: 'linear-gradient(135deg, #666666 0%, #888888 100%)',
          padding: '3px 6px',
          display: 'flex',
          alignItems: 'center',
          gap: 3,
        }}
      >
        <span style={{ fontWeight: 'bold', color: 'white', fontSize: '10px' }}>
          System Prompt
        </span>
      </div>

      {/* Content */}
      <div style={{ padding: 6 }}>
        {/* Template Selection Button */}
        <div style={{ marginBottom: 6 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setShowTemplateDialog(true)}
            disabled={loading}
            sx={{
              width: '100%',
              fontSize: '8px',
              height: '24px',
              textTransform: 'none',
              borderColor: '#ccc',
              color: '#666',
              '&:hover': {
                borderColor: '#999',
                backgroundColor: '#f5f5f5',
              },
            }}
          >
            {loading ? 'Loading...' : 'Select from Templates'}
          </Button>
        </div>

        {/* Text Area */}
        <div style={{ marginBottom: 6 }}>
          <TextField
            multiline
            rows={4}
            value={displayText}
            onChange={handleTextChange}
            placeholder="Enter your system prompt here..."
            variant="outlined"
            size="small"
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                fontSize: '9px',
                padding: '2px',
                '& textarea': {
                  padding: '4px',
                  fontSize: '9px',
                  lineHeight: 1.2,
                },
              },
            }}
          />
          {promptText.length > 200 && (
            <Typography variant="caption" color="text.secondary" style={{ fontSize: '8px', marginTop: '4px', display: 'block' }}>
              {promptText.length} characters - Click "Edit Prompt" to see full text
            </Typography>
          )}
        </div>

        {/* Popup Button */}
        <div style={{ marginBottom: 6 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleOpenPopup}
            sx={{
              width: '100%',
              fontSize: '8px',
              height: '24px',
              textTransform: 'none',
              borderColor: '#1976d2',
              color: '#1976d2',
              '&:hover': {
                borderColor: '#1565c0',
                backgroundColor: '#e3f2fd',
              },
            }}
          >
            Edit Prompt
          </Button>
        </div>

        {/* Output Section */}
        <div>
          <h4 style={{
            margin: '0 0 3px 0',
            textAlign: 'center',
            fontSize: '9px',
            fontWeight: 'bold',
            color: '#333'
          }}>
            Output
          </h4>

          <div className="handle-label-pair" style={{ justifyContent: 'flex-end' }}>
            <label style={{ fontSize: '9px', color: '#333', fontWeight: 'medium' }}>
              Prompt
            </label>
            <Handle
              type="source"
              position={Position.Right}
              id="output"
              style={{
                background: '#7b1fa2',
                width: 8,
                height: 8,
                border: '1px solid white',
                borderRadius: '50%',
              }}
            />
          </div>
        </div>
      </div>

      {/* Template Selection Dialog */}
      <Dialog
        open={showTemplateDialog}
        onClose={() => setShowTemplateDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Select Prompt Template</Typography>
            <IconButton onClick={() => setShowTemplateDialog(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
            {templates.map((template) => (
              <Box
                key={template.id}
                sx={{
                  p: 2,
                  mb: 1,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                    borderColor: '#1976d2',
                  },
                }}
                onClick={() => handleTemplateSelect(template)}
              >
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                  {template.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {template.description}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Category: {template.category} | Popularity: {template.popularity}%
                </Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setShowTemplateDialog(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Popup Dialog for Large Text Editing */}
      <Dialog
        open={showPopup}
        onClose={handleClosePopup}
        maxWidth="lg"
        fullWidth
        fullScreen={false}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Edit System Prompt</Typography>
            <IconButton onClick={handleClosePopup} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <TextField
            multiline
            rows={20}
            value={promptText}
            onChange={handlePopupTextChange}
            placeholder="Enter your system prompt here..."
            variant="outlined"
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '14px',
                '& textarea': {
                  padding: '12px',
                  fontSize: '14px',
                  lineHeight: 1.5,
                  fontFamily: 'monospace',
                },
              },
            }}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClosePopup} variant="contained">
            Save & Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
});